import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  FileText,
  User,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Save,
  Send,
  Wand2,
  Eye,
  Download,
  CheckCircle,
  AlertTriangle,
  Plus,
  Trash2,
  Edit2,
  ChevronDown,
  ChevronUp,
  Building,
  Hammer,
  FileCheck,
  Signature,
  Copy,
  ExternalLink,
  Mail,
  FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProposalBuilder = () => {
  const { api, user } = useAuth();
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [intakeLinks, setIntakeLinks] = useState([]);
  const [showCreateIntakeModal, setShowCreateIntakeModal] = useState(false);
  const [newIntake, setNewIntake] = useState({ client_name: '', client_email: '' });
  const [expandedSections, setExpandedSections] = useState({
    intake: true,
    contract: true,
    payment: true,
    schedule: true,
    scope: true,
    alternates: true,
    exclusions: true,
    internal: false
  });

  const [formData, setFormData] = useState({
    // Contract Structure
    contract_type: 'fixed_price',
    contract_amount: '',
    labor_rate: '',
    material_markup: '',
    not_to_exceed: '',
    
    // Payment Terms
    initial_deposit: '',
    deposit_percentage: '',
    billing_frequency: 'milestone',
    invoice_due_days: 15,
    final_retainage: '',
    
    // Project Schedule
    estimated_start_date: '',
    estimated_duration: '',
    estimated_completion: '',
    
    // Scope of Work
    scope_mobilization: '',
    scope_preparation: '',
    scope_repairs: '',
    scope_installation: '',
    scope_finishes: '',
    scope_cleanup: '',
    
    // Alternates
    alternates: [],
    
    // Exclusions
    exclusions: [],
    
    // Internal Estimating
    estimated_labor_hours: '',
    estimated_material_cost: '',
    estimated_equipment_cost: '',
    estimated_subcontractor_cost: '',
    estimated_total_cost: '',
    target_gross_margin: '',
    target_selling_price: '',
    estimated_profit: ''
  });

  useEffect(() => {
    if (proposalId) {
      fetchProposal();
    } else {
      fetchIntakeLinks();
      setLoading(false);
    }
  }, [proposalId]);

  const fetchProposal = async () => {
    try {
      const response = await api.get(`/proposals/${proposalId}`);
      setProposal(response.data);
      if (response.data.builder_data) {
        setFormData(prev => ({...prev, ...response.data.builder_data}));
      }
    } catch (error) {
      toast.error('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntakeLinks = async () => {
    try {
      const response = await api.get('/proposals/intake-links');
      setIntakeLinks(response.data.links || []);
    } catch (error) {
      console.error('Error fetching intake links:', error);
    }
  };

  const createIntakeLink = async () => {
    if (!newIntake.client_name || !newIntake.client_email) {
      toast.error('Client name and email are required');
      return;
    }
    try {
      const response = await api.post('/proposals/intake-link', null, {
        params: { client_name: newIntake.client_name, client_email: newIntake.client_email }
      });
      toast.success('Intake link created!');
      
      // Copy link to clipboard
      navigator.clipboard.writeText(response.data.share_url);
      toast.success('Link copied to clipboard');
      
      setShowCreateIntakeModal(false);
      setNewIntake({ client_name: '', client_email: '' });
      fetchIntakeLinks();
    } catch (error) {
      toast.error('Failed to create intake link');
    }
  };

  const saveProposal = async () => {
    setSaving(true);
    try {
      if (proposalId) {
        await api.patch(`/proposals/${proposalId}`, { builder_data: formData });
        toast.success('Proposal saved');
      }
    } catch (error) {
      toast.error('Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };

  const generateAIContent = async () => {
    if (!proposalId) return;
    setGenerating(true);
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-ai-content`);
      setProposal(prev => ({
        ...prev,
        executive_summary: response.data.executive_summary,
        detailed_scope: response.data.detailed_scope
      }));
      toast.success('AI content generated! Review and edit as needed.');
    } catch (error) {
      toast.error('Failed to generate AI content');
    } finally {
      setGenerating(false);
    }
  };

  const copyIntakeLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const sendIntakeEmail = async (linkId) => {
    setSendingEmail(linkId);
    try {
      await api.post(`/proposals/intake-link/${linkId}/send-email`);
      toast.success('Email sent to client!');
      fetchIntakeLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const downloadProposalPdf = async () => {
    if (!proposalId) return;
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/proposals/${proposalId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `JAX_Proposal_${proposal?.proposal_number || proposalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Proposal PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadInternalEstimatePdf = async () => {
    if (!proposalId) return;
    try {
      const response = await api.get(`/proposals/${proposalId}/internal-estimate-pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `JAX_Internal_Estimate_${proposal?.proposal_number || proposalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Internal estimate PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const startProposalFromIntake = async (intakeLink) => {
    try {
      const response = await api.post('/proposals', {
        intake_data: intakeLink.intake_data,
        builder_data: formData
      });
      navigate(`/portal/proposal-builder/${response.data.id}`);
      toast.success('Proposal created from intake');
    } catch (error) {
      toast.error('Failed to create proposal');
    }
  };

  const addAlternate = () => {
    setFormData(prev => ({
      ...prev,
      alternates: [...prev.alternates, { description: '', amount: '' }]
    }));
  };

  const removeAlternate = (index) => {
    setFormData(prev => ({
      ...prev,
      alternates: prev.alternates.filter((_, i) => i !== index)
    }));
  };

  const addExclusion = () => {
    setFormData(prev => ({
      ...prev,
      exclusions: [...prev.exclusions, '']
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const calculateProfit = () => {
    const cost = parseFloat(formData.estimated_total_cost) || 0;
    const selling = parseFloat(formData.target_selling_price) || 0;
    const profit = selling - cost;
    const margin = selling > 0 ? (profit / selling * 100) : 0;
    
    setFormData(prev => ({
      ...prev,
      estimated_profit: profit.toFixed(2),
      target_gross_margin: margin.toFixed(1)
    }));
  };

  // Detect auto-triggers from intake
  const triggers = proposal?.triggers || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If no proposal ID, show intake links list
  if (!proposalId) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Proposal Builder</h1>
            <p className="text-gray-500">Create and manage client proposals</p>
          </div>
          <button
            onClick={() => setShowCreateIntakeModal(true)}
            className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg flex items-center gap-2 hover:bg-[#B09265]"
          >
            <Plus className="w-4 h-4" /> Send Intake Form
          </button>
        </div>

        {/* Completed Intakes - Ready for Proposal */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-green-500" />
              Completed Intakes - Ready for Proposal
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {intakeLinks.filter(l => l.status === 'completed').length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No completed intake forms yet. Send an intake link to a client to get started.
              </div>
            ) : (
              intakeLinks.filter(l => l.status === 'completed').map(link => (
                <div key={link.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium">{link.client_name}</p>
                    <p className="text-sm text-gray-500">{link.client_email}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Completed: {new Date(link.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startProposalFromIntake(link)}
                      className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg text-sm hover:bg-[#B09265] flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" /> Start Proposal
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Intakes */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Pending Intake Forms
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {intakeLinks.filter(l => l.status === 'pending').length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No pending intake forms.
              </div>
            ) : (
              intakeLinks.filter(l => l.status === 'pending').map(link => {
                const shareUrl = `${window.location.origin}/intake/${link.token}`;
                return (
                  <div key={link.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="font-medium">{link.client_name}</p>
                      <p className="text-sm text-gray-500">{link.client_email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Sent: {new Date(link.created_at).toLocaleDateString()} • 
                        Expires: {new Date(link.expires_at).toLocaleDateString()}
                        {link.email_sent && <span className="text-green-500 ml-2">• Email sent</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => sendIntakeEmail(link.id)}
                        disabled={sendingEmail === link.id}
                        className="px-3 py-1.5 bg-[#C6A87C] text-white rounded-lg text-sm hover:bg-[#B09265] flex items-center gap-1 disabled:opacity-50"
                      >
                        {sendingEmail === link.id ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Mail className="w-3 h-3" />
                        )}
                        {link.email_sent ? 'Resend' : 'Send Email'}
                      </button>
                      <button
                        onClick={() => copyIntakeLink(shareUrl)}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy Link
                      </button>
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Create Intake Modal */}
        {showCreateIntakeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4">Send Intake Form to Client</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Client Name</label>
                  <input
                    type="text"
                    value={newIntake.client_name}
                    onChange={(e) => setNewIntake({...newIntake, client_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Client Email</label>
                  <input
                    type="email"
                    value={newIntake.client_email}
                    onChange={(e) => setNewIntake({...newIntake, client_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateIntakeModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={createIntakeLink}
                  className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg hover:bg-[#B09265]"
                >
                  Create & Copy Link
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Proposal Builder Form
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Proposal Builder
            {proposal?.proposal_number && (
              <span className="ml-3 text-lg font-normal text-gray-500">#{proposal.proposal_number}</span>
            )}
          </h1>
          <p className="text-gray-500">
            {proposal?.intake_data?.client_name} • {proposal?.intake_data?.property_address}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadProposalPdf}
            disabled={downloadingPdf}
            className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
          >
            {downloadingPdf ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            Download PDF
          </button>
          <button
            onClick={generateAIContent}
            disabled={generating}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50"
          >
            {generating ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
            Generate AI Content
          </button>
          <button
            onClick={saveProposal}
            disabled={saving}
            className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg flex items-center gap-2 hover:bg-[#B09265] disabled:opacity-50"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {/* Auto-Trigger Alerts */}
      {(triggers.historic_addendum || triggers.tm_addendum || triggers.concealed_conditions) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-800 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" /> Auto-Detected Conditions
          </h3>
          <div className="flex flex-wrap gap-2">
            {triggers.historic_addendum && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                Historic Property Addendum Required
              </span>
            )}
            {triggers.tm_addendum && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                T&M Addendum Recommended
              </span>
            )}
            {triggers.concealed_conditions && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                Concealed Conditions Language
              </span>
            )}
          </div>
        </div>
      )}

      {/* AI Generated Content */}
      {(proposal?.executive_summary || proposal?.detailed_scope) && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-medium text-purple-800 flex items-center gap-2 mb-3">
            <Wand2 className="w-5 h-5" /> AI Generated Content (Editable)
          </h3>
          {proposal.executive_summary && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-purple-700 mb-1">Executive Summary</label>
              <textarea
                value={proposal.executive_summary}
                onChange={(e) => setProposal({...proposal, executive_summary: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white"
              />
            </div>
          )}
          {proposal.detailed_scope && (
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Detailed Scope</label>
              <textarea
                value={proposal.detailed_scope}
                onChange={(e) => setProposal({...proposal, detailed_scope: e.target.value})}
                rows={6}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white"
              />
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Client Intake Summary */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('intake')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-[#C6A87C]" /> Client Intake Summary
              </h3>
              {expandedSections.intake ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.intake && proposal?.intake_data && (
              <div className="p-4 pt-0 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Client</p>
                    <p className="font-medium">{proposal.intake_data.client_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Property Type</p>
                    <p className="font-medium capitalize">{proposal.intake_data.property_type?.replace('_', ' ')}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Property Address</p>
                    <p className="font-medium">{proposal.intake_data.property_address}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Project Types</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {proposal.intake_data.project_types?.map(pt => (
                        <span key={pt} className="px-2 py-0.5 bg-gray-100 rounded text-xs capitalize">
                          {pt.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Project Description</p>
                    <p className="text-sm">{proposal.intake_data.project_description}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Budget Range</p>
                    <p className="font-medium capitalize">{proposal.intake_data.budget_range?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Desired Start</p>
                    <p className="font-medium capitalize">{proposal.intake_data.desired_start?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contract Structure */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('contract')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C6A87C]" /> Contract Structure
              </h3>
              {expandedSections.contract ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.contract && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Contract Type</label>
                  <select
                    value={formData.contract_type}
                    onChange={(e) => setFormData({...formData, contract_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="fixed_price">Fixed Price</option>
                    <option value="fixed_price_allowances">Fixed Price with Allowances</option>
                    <option value="time_material">Time & Material</option>
                    <option value="time_material_nte">Time & Material with Not-To-Exceed</option>
                  </select>
                </div>
                
                {(formData.contract_type === 'fixed_price' || formData.contract_type === 'fixed_price_allowances') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Contract Amount</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        value={formData.contract_amount}
                        onChange={(e) => setFormData({...formData, contract_amount: e.target.value})}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
                
                {(formData.contract_type === 'time_material' || formData.contract_type === 'time_material_nte') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Labor Rate ($/hr)</label>
                      <input
                        type="number"
                        value={formData.labor_rate}
                        onChange={(e) => setFormData({...formData, labor_rate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                        placeholder="65.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Material Markup (%)</label>
                      <input
                        type="number"
                        value={formData.material_markup}
                        onChange={(e) => setFormData({...formData, material_markup: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                        placeholder="15"
                      />
                    </div>
                  </>
                )}
                
                {formData.contract_type === 'time_material_nte' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Not-To-Exceed Amount</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        value={formData.not_to_exceed}
                        onChange={(e) => setFormData({...formData, not_to_exceed: e.target.value})}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Terms */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('payment')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#C6A87C]" /> Payment Terms
              </h3>
              {expandedSections.payment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.payment && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Initial Deposit ($)</label>
                    <input
                      type="number"
                      value={formData.initial_deposit}
                      onChange={(e) => setFormData({...formData, initial_deposit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Or Deposit (%)</label>
                    <input
                      type="number"
                      value={formData.deposit_percentage}
                      onChange={(e) => setFormData({...formData, deposit_percentage: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Billing Frequency</label>
                  <select
                    value={formData.billing_frequency}
                    onChange={(e) => setFormData({...formData, billing_frequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="milestone">Milestone Based</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Invoice Due (Days)</label>
                    <input
                      type="number"
                      value={formData.invoice_due_days}
                      onChange={(e) => setFormData({...formData, invoice_due_days: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Final Retainage ($)</label>
                    <input
                      type="number"
                      value={formData.final_retainage}
                      onChange={(e) => setFormData({...formData, final_retainage: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Project Schedule */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('schedule')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C6A87C]" /> Project Schedule
              </h3>
              {expandedSections.schedule ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.schedule && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Start Date</label>
                  <input
                    type="date"
                    value={formData.estimated_start_date}
                    onChange={(e) => setFormData({...formData, estimated_start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Duration</label>
                  <input
                    type="text"
                    value={formData.estimated_duration}
                    onChange={(e) => setFormData({...formData, estimated_duration: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    placeholder="e.g., 4-6 weeks"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Completion</label>
                  <input
                    type="date"
                    value={formData.estimated_completion}
                    onChange={(e) => setFormData({...formData, estimated_completion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Scope of Work */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('scope')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <Hammer className="w-5 h-5 text-[#C6A87C]" /> Scope of Work
              </h3>
              {expandedSections.scope ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.scope && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-4">
                {['mobilization', 'preparation', 'repairs', 'installation', 'finishes', 'cleanup'].map(phase => (
                  <div key={phase}>
                    <label className="block text-sm font-medium mb-1 capitalize">{phase === 'repairs' ? 'Repairs & Restoration' : phase === 'mobilization' ? 'Mobilization & Site Protection' : phase}</label>
                    <textarea
                      value={formData[`scope_${phase}`]}
                      onChange={(e) => setFormData({...formData, [`scope_${phase}`]: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      placeholder={`Describe ${phase} work...`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Alternates */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('alternates')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#C6A87C]" /> Alternates
              </h3>
              {expandedSections.alternates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.alternates && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-3">
                {formData.alternates.map((alt, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={alt.description}
                      onChange={(e) => {
                        const updated = [...formData.alternates];
                        updated[idx].description = e.target.value;
                        setFormData({...formData, alternates: updated});
                      }}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      placeholder="Alternate description"
                    />
                    <input
                      type="number"
                      value={alt.amount}
                      onChange={(e) => {
                        const updated = [...formData.alternates];
                        updated[idx].amount = e.target.value;
                        setFormData({...formData, alternates: updated});
                      }}
                      className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      placeholder="Amount"
                    />
                    <button
                      onClick={() => removeAlternate(idx)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addAlternate}
                  className="text-sm text-[#C6A87C] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Alternate
                </button>
              </div>
            )}
          </div>

          {/* Internal Estimating (Hidden from Client) */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={() => toggleSection('internal')}
              className="w-full p-4 flex justify-between items-center"
            >
              <h3 className="font-semibold flex items-center gap-2">
                <Building className="w-5 h-5 text-gray-600" /> 
                Internal Estimate Worksheet
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Hidden from Client</span>
              </h3>
              {expandedSections.internal ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.internal && (
              <div className="p-4 pt-0 border-t border-gray-200 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Est. Labor Hours</label>
                    <input
                      type="number"
                      value={formData.estimated_labor_hours}
                      onChange={(e) => setFormData({...formData, estimated_labor_hours: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Est. Material Cost</label>
                    <input
                      type="number"
                      value={formData.estimated_material_cost}
                      onChange={(e) => setFormData({...formData, estimated_material_cost: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Est. Equipment Cost</label>
                    <input
                      type="number"
                      value={formData.estimated_equipment_cost}
                      onChange={(e) => setFormData({...formData, estimated_equipment_cost: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Est. Subcontractor Cost</label>
                    <input
                      type="number"
                      value={formData.estimated_subcontractor_cost}
                      onChange={(e) => setFormData({...formData, estimated_subcontractor_cost: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Est. Total Cost</label>
                      <input
                        type="number"
                        value={formData.estimated_total_cost}
                        onChange={(e) => setFormData({...formData, estimated_total_cost: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Target Selling Price</label>
                      <input
                        type="number"
                        value={formData.target_selling_price}
                        onChange={(e) => setFormData({...formData, target_selling_price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      />
                    </div>
                  </div>
                  <button
                    onClick={calculateProfit}
                    className="mt-3 text-sm text-[#C6A87C] hover:underline"
                  >
                    Calculate Profit & Margin
                  </button>
                  {formData.estimated_profit && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <div className="flex justify-between">
                        <span className="text-sm text-green-700">Estimated Profit:</span>
                        <span className="font-semibold text-green-700">${parseFloat(formData.estimated_profit).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-green-700">Gross Margin:</span>
                        <span className="font-semibold text-green-700">{formData.target_gross_margin}%</span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={downloadInternalEstimatePdf}
                    className="mt-4 w-full px-4 py-2 bg-gray-700 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-gray-800"
                  >
                    <FileDown className="w-4 h-4" /> Download Internal Estimate PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalBuilder;
